import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import axios from 'axios'
import { useBrand } from '../context/BrandContext'
import { colors, radius, s, ui, chartPalette, tone, chartBase, lineGradient, areaGradient } from '../styles/theme'
import Chart from '../components/Chart'
import { useIsMobile } from '../hooks/useViewport'

const ANALYTICS = process.env.REACT_APP_ANALYTICS_URL || 'http://localhost:8100'

const TABS = [
  { key: 'ga4', label: 'GA4', note: 'Google Analytics 4' },
  { key: 'metrica', label: 'Metrica', note: 'Yandex Metrica' },
  { key: 'cip', label: 'Ticaret (CIP)', note: 'T-Soft sipariş verisi' },
]

const SUGGESTIONS = [
  'Bu hafta en önemli problem ne?',
  'Neden satış düştü?',
  'Hangi kategori büyüyor?',
  'Geçen haftaya göre ne değişti?',
  'Hangi kampanyayı durdurmalıyım?',
]

const BUCKETS = [
  { key: 'iyiye_gidenler', label: 'İyiye Gidenler', short: 'İyiye gidenler' },
  { key: 'korunmasi_gerekenler', label: 'Korunması Gerekenler', short: 'Korunması gerekenler' },
  { key: 'acil_mudahale', label: 'Acil Müdahale Edilmesi Gerekenler', short: 'Acil müdahale' },
]

const STAGES = ['Soruyu anlıyor', 'İlgili veriyi çekiyor', 'Dönemleri karşılaştırıyor', 'Yanıtı yazıyor']

const KEYFRAMES = `
@keyframes sa-spin{to{transform:rotate(360deg)}}
@keyframes sa-sweep{0%{background-position:-360px 0}100%{background-position:360px 0}}
@keyframes sa-rise{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
`

const TOOL_TR = {
  get_overview: 'özet',
  get_channels: 'kanallar',
  get_funnel: 'huni',
  get_products: 'ürünler',
  get_campaigns: 'kampanyalar',
  get_pages: 'sayfalar',
  compare_periods: 'dönem karşılaştırma',
  get_anomalies: 'anomaliler',
  get_customer_portfolio: 'müşteri portföyü',
}

const fmt = (n) => new Intl.NumberFormat('tr-TR').format(Math.round(n || 0))
const dec = (n, d = 2) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d }).format(n || 0)
const money = (n) => {
  const v = n || 0
  if (Math.abs(v) >= 1e6) return dec(v / 1e6, 1) + 'M ₺'
  if (Math.abs(v) >= 1e3) return fmt(v) + ' ₺'
  return dec(v, 0) + ' ₺'
}
const pct = (n) => '%' + dec(n || 0, 2)
const dur = (s) => {
  const total = Math.round(s || 0)
  const m = Math.floor(total / 60)
  const sec = total % 60
  return m > 0 ? `${m}dk ${sec}sn` : `${sec}sn`
}
const SAKLAMA_GUN = 60
const isoDaysAgo = (d) => {
  const t = new Date()
  t.setDate(t.getDate() - d)
  return t.toISOString().slice(0, 10)
}
const chartH = (h) => (typeof window !== 'undefined' && window.innerWidth <= 768 ? 210 : h)

const st = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' },
  title: s.pageTitle,
  sub: { ...s.pageSubtitle, margin: '3px 0 0' },
  controls: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  date: { ...s.input, width: 'auto', padding: '7px 10px' },
  refresh: (busy) => ({ ...s.btnSecondary, padding: '7px 14px', color: busy ? colors.textMuted : colors.textPrimary, cursor: busy ? 'default' : 'pointer' }),
  tabs: ui.tabBar,
  tab: ui.tab,
  meta: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px', fontSize: '12px', color: colors.textMuted },
  chip: (mark) => (mark ? ui.chipStrong : ui.chip),
  dot: { width: '6px', height: '6px', borderRadius: '50%', background: colors.border, flexShrink: 0 },
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '18px' },
  kpi: ui.statBox,
  kpiLabel: { ...ui.statLabel, marginBottom: '9px', minHeight: '2.4em' },
  kpiVal: { ...ui.statValue, fontSize: '22px', margin: 0, lineHeight: 1 },
  groupHead: { display: 'flex', alignItems: 'baseline', gap: '10px', margin: '2px 0 10px', flexWrap: 'wrap' },
  groupTitle: { ...s.sectionTitle, fontSize: '13px', margin: 0 },
  groupHint: { ...s.sectionSubtitle, color: colors.textMuted },
  cols: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' },
  card: { ...s.card, padding: '19px 20px 20px' },
  h2: { ...s.sectionTitle, fontSize: '14px', margin: 0 },
  note: { ...s.sectionSubtitle, color: colors.textMuted, margin: '2px 0 14px' },
  table: s.table,
  th: s.th,
  thR: { ...s.th, textAlign: 'right', whiteSpace: 'nowrap' },
  td: s.td,
  tdR: { ...s.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  tdMuted: { ...s.td, color: colors.textSecondary, fontSize: '12px' },
  scroll: { overflowX: 'auto' },
  split: { display: 'flex', border: `1px solid ${colors.border}`, borderRadius: radius.lg, overflow: 'hidden', marginTop: '16px' },
  seg: { padding: '16px 18px', flex: 1, minWidth: 0 },
  segTop: { display: 'flex', alignItems: 'center', gap: '8px', color: colors.textSecondary, fontSize: '12.5px', marginBottom: '8px' },
  segVal: { ...ui.statValue, fontSize: '19px', margin: 0 },
  segMeta: { ...ui.statLabel, color: colors.textMuted, marginTop: '3px' },
  msg: { ...ui.notice, padding: '32px', textAlign: 'center', fontSize: '14px' },
  msgError: { ...s.alertDanger, padding: '32px', textAlign: 'center', fontSize: '14px' },
  ai: { ...s.card, padding: '18px 20px' },
  aiHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  aiTitle: { ...s.sectionTitle, fontSize: '14px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' },
  aiBtns: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  aiBtn: (disabled, ghost) => ({
    ...(ghost ? ui.btnQuiet : ui.btnSm),
    padding: '7px 14px',
    fontSize: '13px',
    cursor: disabled ? 'default' : 'pointer',
    color: disabled ? colors.textMuted : (ghost ? colors.textSecondary : colors.textPrimary),
  }),
  aiHeadline: { fontSize: '15px', fontWeight: 600, color: colors.textPrimary, margin: '16px 0 8px' },
  aiSummary: { fontSize: '13px', color: colors.textSecondary, margin: '0 0 16px', lineHeight: 1.6 },
  aiCols: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' },
  aiColTitle: { fontSize: '12px', fontWeight: 600, color: colors.textPrimary, margin: '0 0 8px' },
  aiList: { margin: 0, paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '6px' },
  aiItem: { fontSize: '13px', color: colors.textPrimary, lineHeight: 1.5 },
  brCard: { ...s.card, padding: '22px 24px 24px', marginBottom: '18px' },
  brTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap' },
  brMetaRow: { display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap', margin: '5px 0 0' },
  brMeta: { fontSize: '12px', color: colors.textMuted },
  brSep: { fontSize: '12px', color: colors.border },
  brLede: { maxWidth: '70ch', margin: '20px 0 0' },
  brHeadline: { fontSize: '17px', fontWeight: 600, color: colors.textPrimary, margin: 0, lineHeight: 1.45, letterSpacing: '-0.01em' },
  brSummary: { fontSize: '13.5px', color: colors.textSecondary, margin: '9px 0 0', lineHeight: 1.75 },
  brKpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: '10px', margin: '22px 0 0' },
  brKpi: { ...ui.statBox, padding: '13px 14px' },
  brKpiVal: { ...ui.statValue, fontSize: '20px', margin: 0, lineHeight: 1 },
  brDelta: (change) => ({
    ...ui.numMuted,
    marginTop: '7px', fontSize: '12px', fontWeight: 600,
    color: change == null ? colors.textMuted : colors.textSecondary,
  }),
  brTally: { display: 'flex', gap: '7px', flexWrap: 'wrap', margin: '24px 0 0' },
  brTallyChip: (active, disabled) => ({
    ...(active ? ui.chipStrong : ui.chip),
    gap: '7px',
    padding: '6px 12px',
    cursor: disabled ? 'default' : 'pointer',
    fontWeight: active ? 600 : 500,
    opacity: disabled ? 0.55 : 1,
  }),
  brTallyNum: { fontVariantNumeric: 'tabular-nums', opacity: 0.75 },
  brSection: { marginTop: '24px' },
  brSecHead: { display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '12px' },
  brDot: { width: '6px', height: '6px', borderRadius: '50%', background: colors.border, flexShrink: 0 },
  brSecName: { fontSize: '13px', fontWeight: 600, color: colors.textPrimary },
  brSecCount: { ...ui.numMuted, fontSize: '11.5px', color: colors.textMuted },
  brItems: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: '12px' },
  brItemCard: { background: colors.bgPage, border: `1px solid ${colors.borderLight}`, borderRadius: radius.md, padding: '15px 16px 16px' },
  brItemTitle: { fontSize: '13.5px', fontWeight: 600, color: colors.textPrimary, marginBottom: '7px', lineHeight: 1.45 },
  brItemDetail: { fontSize: '12.5px', color: colors.textSecondary, lineHeight: 1.7 },
  brItemAction: { fontSize: '12px', color: colors.textSecondary, marginTop: '11px', paddingTop: '11px', borderTop: `1px solid ${colors.border}`, lineHeight: 1.6 },
  brActionLabel: { color: colors.textPrimary, fontWeight: 600 },
  brNone: { ...ui.emptyText, fontSize: '12.5px' },

  askCard: { ...s.card, padding: '20px 22px 22px' },
  askHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' },
  askHint: { ...s.sectionSubtitle, color: colors.textMuted, margin: '5px 0 0' },
  askBar: { display: 'flex', gap: '8px', margin: '16px 0 0', flexWrap: 'wrap' },
  askInput: (busy) => ({
    ...s.input, width: 'auto', flex: '1 1 180px', minWidth: 0, padding: '11px 14px',
    fontSize: '13.5px', background: busy ? colors.bgPage : colors.bgCard,
  }),
  askSend: (disabled) => ({
    ...s.btnPrimary,
    padding: '0 20px',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    cursor: disabled ? 'default' : 'pointer',
    ...(disabled ? { background: colors.borderLight, color: colors.textMuted } : null),
  }),
  sugLabel: { fontSize: '12px', color: colors.textMuted, margin: '20px 0 9px' },
  sugWrap: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(235px, 1fr))', gap: '10px' },
  sugCard: {
    ...s.btnSecondary,
    justifyContent: 'space-between', gap: '10px',
    textAlign: 'left', padding: '13px 15px',
    fontSize: '13px', lineHeight: 1.45,
  },
  sugText: { flex: 1, fontWeight: 500 },
  sugArrow: { color: colors.textMuted, fontSize: '14px', fontWeight: 700, flexShrink: 0 },
  histRow: { display: 'flex', gap: '6px', flexWrap: 'wrap', margin: '18px 0 0' },
  histChip: (active) => ({
    ...(active ? ui.chipStrong : ui.chip),
    gap: '6px',
    padding: '5px 11px',
    cursor: 'pointer',
    fontWeight: active ? 600 : 500,
    maxWidth: '100%',
  }),
  histNum: { fontSize: '10.5px', fontWeight: 600, opacity: 0.65, fontVariantNumeric: 'tabular-nums' },
  qaWrap: { marginTop: '18px', paddingTop: '18px', borderTop: `1px solid ${colors.borderLight}`, animation: 'sa-rise .22s ease-out' },
  qaQ: { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '13px' },
  qaQMark: { color: colors.textMuted, fontSize: '15px', fontWeight: 700, lineHeight: 1.45, flexShrink: 0 },
  qaQText: { fontSize: '15px', fontWeight: 600, color: colors.textPrimary, margin: 0, lineHeight: 1.45 },
  qaA: { fontSize: '13.5px', color: colors.textPrimary, lineHeight: 1.8, margin: 0, whiteSpace: 'pre-wrap', maxWidth: '74ch' },
  qaTools: { fontSize: '11.5px', color: colors.textMuted, margin: '15px 0 0' },
  loadWrap: { marginTop: '18px', paddingTop: '18px', borderTop: `1px solid ${colors.borderLight}` },
  loadTop: { display: 'flex', alignItems: 'center', gap: '12px' },
  ringWrap: { position: 'relative', width: '18px', height: '18px', flexShrink: 0 },
  ringGlow: { position: 'absolute', inset: '-6px', borderRadius: '50%', background: colors.primaryLight },
  ring: { position: 'absolute', inset: 0, borderRadius: '50%', border: `2px solid ${colors.primaryLight}`, borderTopColor: colors.primary, animation: 'sa-spin .75s linear infinite' },
  loadText: { fontSize: '13px', fontWeight: 500, color: colors.textSecondary },
  loadBars: { display: 'flex', flexDirection: 'column', gap: '10px', margin: '18px 0 0', maxWidth: '74ch' },
  bar: (w) => ({
    display: 'block', height: '10px', borderRadius: radius.sm, width: w,
    background: `linear-gradient(90deg, ${colors.borderLight} 0%, ${colors.border} 38%, ${colors.bgCard} 50%, ${colors.border} 62%, ${colors.borderLight} 100%)`,
    backgroundSize: '360px 100%',
    animation: 'sa-sweep 1.15s linear infinite',
  }),
  ghostBtn: { ...ui.btnQuiet, padding: '7px 13px', fontSize: '12.5px' },
  reconcile: {
    ...ui.notice,
    margin: '12px 0 0', padding: '11px 13px',
    fontSize: '12px', lineHeight: 1.65,
  },
  reconcileNum: { ...ui.num, fontSize: '12px' },
}

const axisCat = chartBase.xAxisStyle
const axisVal = chartBase.yAxisStyle

function KpiGrid({ items }) {
  return (
    <div className="sx-grid-stat" style={st.kpiRow}>
      {items.map((k, i) => (
        <div key={i} style={st.kpi}>
          <div style={st.kpiLabel}>{k.label}</div>
          <div style={st.kpiVal}>{k.val}</div>
        </div>
      ))}
    </div>
  )
}

function Empty({ text }) {
  return <p style={{ ...st.note, margin: '10px 0 0' }}>{text}</p>
}

function Skeleton({ widths }) {
  return (
    <div style={st.loadBars}>
      {widths.map((w, i) => <span key={i} style={st.bar(w)} />)}
    </div>
  )
}

function Briefing({ data, onGenerate, busy }) {
  const [filter, setFilter] = useState('all')

  const counts = useMemo(() => {
    const c = { all: 0 }
    BUCKETS.forEach(b => {
      c[b.key] = (data?.[b.key] || []).length
      c.all += c[b.key]
    })
    return c
  }, [data])

  const meta = useMemo(() => {
    const d = data?.donem
    if (!d) return []
    return [d.baslik || d.guncel?.[1], d.gun_adi, d.kiyas_aciklamasi || 'önceki dönemle karşılaştırmalı'].filter(Boolean)
  }, [data])

  const sections = filter === 'all' ? BUCKETS : BUCKETS.filter(b => b.key === filter)

  return (
    <div style={st.brCard}>
      <div style={st.brTop}>
        <div>
          <h2 style={st.h2}>Günlük Durum</h2>
          <div style={st.brMetaRow}>
            {meta.length > 0
              ? meta.map((m, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span style={st.brSep}>·</span>}
                  <span style={st.brMeta}>{m}</span>
                </React.Fragment>
              ))
              : <span style={st.brMeta}>{busy ? 'Hazırlanıyor…' : 'Henüz brifing üretilmedi.'}</span>}
          </div>
        </div>
        <button style={st.refresh(busy)} onClick={onGenerate} disabled={busy}>
          {busy ? 'Hazırlanıyor…' : 'Yeniden hazırla'}
        </button>
      </div>

      {busy && !data && <Skeleton widths={['100%', '86%', '62%']} />}

      {(data?.headline || data?.summary) && (
        <div style={st.brLede}>
          {data.headline && <p style={st.brHeadline}>{data.headline}</p>}
          {data.summary && <p style={st.brSummary}>{data.summary}</p>}
        </div>
      )}

      {data?.kpi?.length > 0 && (
        <div className="sx-grid-stat" style={st.brKpiRow}>
          {data.kpi.map((k, i) => (
            <div key={i} style={st.brKpi}>
              <div style={st.kpiLabel}>{k.etiket}</div>
              <div style={st.brKpiVal}>
                {k.tip === 'para' ? money(k.deger) : k.tip === 'yuzde' ? pct(k.deger) : fmt(k.deger)}
              </div>
              <div style={st.brDelta(k.degisim)}>
                {k.degisim == null ? '—' : `${k.degisim > 0 ? '▲' : k.degisim < 0 ? '▼' : '•'} ${dec(Math.abs(k.degisim), 1)}%`}
              </div>
            </div>
          ))}
        </div>
      )}

      {counts.all > 0 && (
        <>
          <div style={st.brTally}>
            <button style={st.brTallyChip(filter === 'all', false)} onClick={() => setFilter('all')}>
              Tümü <span style={st.brTallyNum}>{counts.all}</span>
            </button>
            {BUCKETS.map(b => (
              <button
                key={b.key}
                style={st.brTallyChip(filter === b.key, counts[b.key] === 0)}
                onClick={() => counts[b.key] > 0 && setFilter(b.key)}
                disabled={counts[b.key] === 0}
              >
                <span style={st.brDot} />
                {b.short} <span style={st.brTallyNum}>{counts[b.key]}</span>
              </button>
            ))}
          </div>

          {sections.map(b => (
            <div key={b.key} style={st.brSection}>
              <div style={st.brSecHead}>
                <span style={st.brDot} />
                <span style={st.brSecName}>{b.label}</span>
                <span style={st.brSecCount}>{counts[b.key]}</span>
              </div>
              {counts[b.key] === 0 ? (
                <p style={st.brNone}>Bu başlıkta bulgu yok.</p>
              ) : (
                <div style={st.brItems}>
                  {(data[b.key] || []).map((item, i) => (
                    <div key={i} style={st.brItemCard}>
                      <div className="sx-break" style={st.brItemTitle}>{item.baslik}</div>
                      <div className="sx-break" style={st.brItemDetail}>{item.detay}</div>
                      {item.aksiyon && (
                        <div style={st.brItemAction}>
                          <span style={st.brActionLabel}>Aksiyon</span> · {item.aksiyon}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {data && counts.all === 0 && <Empty text="Bu dönemde öne çıkan bulgu üretilmedi." />}
    </div>
  )
}

function Thinking() {
  const [stage, setStage] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setStage(p => Math.min(p + 1, STAGES.length - 1)), 2200)
    return () => clearInterval(t)
  }, [])
  return (
    <div style={st.loadWrap}>
      <div style={st.loadTop}>
        <span style={st.ringWrap}>
          <span style={st.ringGlow} />
          <span style={st.ring} />
        </span>
        <span style={st.loadText}>{STAGES[stage]}…</span>
      </div>
      <Skeleton widths={['100%', '94%', '78%', '55%']} />
    </div>
  )
}

function AskPanel({ thread, shown, shownIdx, isLatest, onSelect, onClear, question, setQuestion, asking, askError, onAsk }) {
  const anchor = useRef(null)
  const count = thread.length

  useEffect(() => {
    if (count > 0 && anchor.current) anchor.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [count])

  return (
    <div style={st.askCard}>
      <div style={st.askHead}>
        <div>
          <h2 style={st.h2}>Veriye Sor</h2>
          <p style={st.askHint}>Seçili dönemin verisine bakar, yanıtı o veriyle sınırlı tutar.</p>
        </div>
        {count > 0 && (
          <button style={st.ghostBtn} onClick={onClear} disabled={asking}>Sohbeti temizle</button>
        )}
      </div>

      <div style={st.askBar}>
        <input
          style={st.askInput(asking)}
          placeholder={count ? 'Kendi sorunu yaz…' : 'Örn: 3 Ağustos’taki düşüşün sebebi nedir?'}
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') onAsk(question) }}
          disabled={asking}
        />
        <button style={st.askSend(asking || !question.trim())} onClick={() => onAsk(question)} disabled={asking || !question.trim()}>
          {asking ? 'Soruluyor…' : 'Sor'}
        </button>
      </div>

      {!asking && count === 0 && (
        <>
          <p style={st.sugLabel}>Örnek sorular</p>
          <div style={st.sugWrap}>
            {SUGGESTIONS.slice(0, 3).map((q, i) => (
              <button key={i} style={st.sugCard} onClick={() => onAsk(q)}>
                <span style={st.sugText}>{q}</span>
                <span style={st.sugArrow}>→</span>
              </button>
            ))}
          </div>
        </>
      )}

      {count > 1 && (
        <div style={st.histRow}>
          {thread.map((t, i) => (
            <button key={i} style={st.histChip(i === shownIdx)} onClick={() => onSelect(i)} title={t.question}>
              <span style={st.histNum}>{i + 1}</span>
              {t.question.length > 34 ? t.question.slice(0, 34) + '…' : t.question}
            </button>
          ))}
        </div>
      )}

      <div ref={anchor} />

      {asking && <Thinking />}

      {!asking && shown && (
        <div style={st.qaWrap}>
          <div style={st.qaQ}>
            <span style={st.qaQMark}>›</span>
            <p style={st.qaQText}>{shown.question}</p>
          </div>
          <p className="sx-break" style={st.qaA}>{shown.answer}</p>
          {shown.tools?.length > 0 && (
            <p style={st.qaTools}>
              Kullanılan veri: {[...new Set(shown.tools.map(t => TOOL_TR[t.name] || t.name))].join(' · ')}
            </p>
          )}
        </div>
      )}

      {!asking && shown && !isLatest && (
        <button style={{ ...st.ghostBtn, marginTop: '16px' }} onClick={() => onSelect(null)}>← Son yanıta dön</button>
      )}

      {!asking && shown && isLatest && shown.suggestions?.length > 0 && (
        <>
          <p style={st.sugLabel}>Bunu da sorabilirsin</p>
          <div style={st.sugWrap}>
            {shown.suggestions.map((q, j) => (
              <button key={j} style={st.sugCard} onClick={() => onAsk(q)}>
                <span style={st.sugText}>{q}</span>
                <span style={st.sugArrow}>→</span>
              </button>
            ))}
          </div>
        </>
      )}

      {askError && <p style={{ fontSize: '13px', color: colors.dangerText, margin: '14px 0 0' }}>{askError}</p>}
    </div>
  )
}

function SumNote({ rows, total, boyut }) {
  const sum = (rows || []).reduce((s, r) => s + (r.sessions || 0), 0)
  if (!total || !sum || sum <= total) return null
  return (
    <p style={st.reconcile}>
      Bu kırılımdaki oturum toplamı <span style={st.reconcileNum}>{fmt(sum)}</span>, özetteki toplam oturum ise{' '}
      <span style={st.reconcileNum}>{fmt(total)}</span> (%{dec(sum / total * 100 - 100, 1)} fazla).
      Bir oturum sırasında birden fazla {boyut} ile temas edilirse analitik aracı o oturumu her birinin altında ayrı sayar;
      bu yüzden kırılım toplamı özet toplamdan büyük olur. Satın alma ve ciro her iki raporda da birebir aynıdır.
    </p>
  )
}

function SourceTab({ data, sourceLabel }) {
  const o = data.overview

  const trendOption = useMemo(() => ({
    color: chartPalette,
    textStyle: chartBase.textStyle,
    tooltip: { ...chartBase.tooltip },
    legend: { data: ['Oturum', 'Ciro'], bottom: 0, icon: 'roundRect', itemWidth: 9, itemHeight: 9, textStyle: { color: colors.textSecondary, fontSize: 11 } },
    grid: { left: 8, right: 8, top: 16, bottom: 34, containLabel: true },
    xAxis: { type: 'category', data: data.timeseries.map(p => p.date.slice(5)), ...axisCat, boundaryGap: false },
    yAxis: [
      { type: 'value', ...axisVal, name: '' },
      { type: 'value', ...axisVal, splitLine: { show: false } },
    ],
    series: [
      {
        name: 'Oturum', type: 'line', smooth: true, showSymbol: false,
        data: data.timeseries.map(p => p.sessions),
        lineStyle: { width: 2.5, color: lineGradient }, itemStyle: { color: colors.primary },
        areaStyle: { color: areaGradient },
      },
      {
        name: 'Ciro', type: 'line', yAxisIndex: 1, smooth: true, showSymbol: false,
        data: data.timeseries.map(p => p.revenue),
        lineStyle: { width: 2, color: colors.secondary }, itemStyle: { color: colors.secondary },
      },
    ],
  }), [data.timeseries])

  const funnelOption = useMemo(() => ({
    color: chartPalette,
    textStyle: chartBase.textStyle,
    tooltip: { ...chartBase.tooltip, trigger: 'item', formatter: (p) => `${p.name}<br/>${fmt(p.value)} (${dec(p.data.fromTop, 1)}%)` },
    series: [{
      type: 'funnel', left: '5%', right: '5%', top: 10, bottom: 10, minSize: '18%',
      sort: 'none', gap: 3,
      label: { position: 'inside', color: colors.textOnPrimary, fontSize: 12, fontWeight: 500, formatter: (p) => `${p.name}  ${fmt(p.value)}` },
      itemStyle: { borderWidth: 0 },
      data: data.funnel.map((f, i) => ({
        name: f.label, value: f.count, fromTop: f.from_top_pct,
        itemStyle: { color: tone(i) },
      })),
    }],
  }), [data.funnel])

  const channelOption = useMemo(() => {
    const rows = [...data.channels].sort((a, b) => a.sessions - b.sessions)
    return {
      color: chartPalette,
      textStyle: chartBase.textStyle,
      tooltip: { ...chartBase.tooltip, axisPointer: { type: 'shadow' }, formatter: (ps) => {
        const p = ps[0]
        const row = rows[p.dataIndex]
        return `${row.channel}<br/>Oturum: ${fmt(row.sessions)}<br/>Satın alma: ${fmt(row.purchases)}<br/>Ciro: ${money(row.revenue)}<br/>Dönüşüm: ${pct(row.conversion_rate_pct)}`
      } },
      grid: { left: 8, right: 24, top: 8, bottom: 8, containLabel: true },
      xAxis: { type: 'value', ...axisVal },
      yAxis: { type: 'category', data: rows.map(r => r.channel), ...axisCat },
      series: [{
        type: 'bar', data: rows.map(r => r.sessions), barMaxWidth: 18,
        itemStyle: { color: lineGradient, borderRadius: [0, 4, 4, 0] },
        label: { show: true, position: 'right', color: colors.textSecondary, fontSize: 11, formatter: (p) => fmt(p.value) },
      }],
    }
  }, [data.channels])

  const deviceOption = useMemo(() => ({
    color: chartPalette,
    textStyle: chartBase.textStyle,
    tooltip: { ...chartBase.tooltip, trigger: 'item', formatter: (p) => `${p.name}<br/>${fmt(p.value)} oturum (${dec(p.percent, 1)}%)` },
    legend: { bottom: 0, icon: 'roundRect', itemWidth: 9, itemHeight: 9, textStyle: { color: colors.textSecondary, fontSize: 11 } },
    series: [{
      type: 'pie', radius: ['52%', '76%'], center: ['50%', '44%'], avoidLabelOverlap: true,
      itemStyle: { borderColor: colors.bgCard, borderWidth: 2 },
      label: { show: false }, labelLine: { show: false },
      data: data.devices.map((d, i) => ({ name: d.device, value: d.sessions, itemStyle: { color: tone(i) } })),
    }],
  }), [data.devices])

  const geoOption = useMemo(() => {
    const rows = [...data.geo].slice(0, 10).sort((a, b) => a.sessions - b.sessions)
    return {
      color: chartPalette,
      textStyle: chartBase.textStyle,
      tooltip: { ...chartBase.tooltip, axisPointer: { type: 'shadow' } },
      grid: { left: 8, right: 24, top: 8, bottom: 8, containLabel: true },
      xAxis: { type: 'value', ...axisVal },
      yAxis: { type: 'category', data: rows.map(r => r.city), ...axisCat },
      series: [{
        type: 'bar', data: rows.map(r => r.sessions), barMaxWidth: 16,
        itemStyle: { color: lineGradient, borderRadius: [0, 4, 4, 0] },
        label: { show: true, position: 'right', color: colors.textSecondary, fontSize: 11, formatter: (p) => fmt(p.value) },
      }],
    }
  }, [data.geo])

  const brandOption = useMemo(() => {
    const rows = [...data.brands].slice(0, 10).sort((a, b) => a.revenue - b.revenue)
    return {
      color: chartPalette,
      textStyle: chartBase.textStyle,
      tooltip: { ...chartBase.tooltip, axisPointer: { type: 'shadow' }, formatter: (ps) => {
        const row = rows[ps[0].dataIndex]
        return `${row.name}<br/>Ciro: ${money(row.revenue)}<br/>Satış: ${fmt(row.purchased)} adet`
      } },
      grid: { left: 8, right: 30, top: 8, bottom: 8, containLabel: true },
      xAxis: { type: 'value', ...axisVal },
      yAxis: { type: 'category', data: rows.map(r => r.name), ...axisCat },
      series: [{
        type: 'bar', data: rows.map(r => r.revenue), barMaxWidth: 16,
        itemStyle: { color: lineGradient, borderRadius: [0, 4, 4, 0] },
        label: { show: true, position: 'right', color: colors.textSecondary, fontSize: 11, formatter: (p) => money(p.value) },
      }],
    }
  }, [data.brands])

  const paidCampaigns = data.campaigns.filter(c => c.cost > 0)

  return (
    <>
      <div style={st.groupHead}>
        <span style={st.groupTitle}>Özet</span>
        <span style={st.groupHint}>{sourceLabel} · seçili dönem</span>
      </div>
      <KpiGrid items={[
        { label: 'Oturum', val: fmt(o.sessions) },
        { label: 'Kullanıcı', val: fmt(o.users) },
        { label: 'Yeni kullanıcı', val: fmt(o.new_users) },
        { label: 'Sayfa görüntüleme', val: fmt(o.pageviews) },
      ]} />
      <KpiGrid items={[
        { label: 'Satın alma', val: fmt(o.purchases) },
        { label: 'Ciro', val: money(o.revenue) },
        { label: 'Dönüşüm oranı', val: pct(o.conversion_rate_pct) },
        { label: 'Sepet ortalaması', val: money(o.aov) },
      ]} />
      <KpiGrid items={[
        { label: 'Hemen çıkma', val: pct(o.bounce_rate_pct) },
        { label: 'Etkileşimli oturum', val: pct(o.engagement_rate_pct) },
        { label: 'Ort. oturum süresi', val: dur(o.avg_session_sec) },
        { label: 'Dönen kullanıcı', val: fmt(o.returning_users) },
      ]} />

      {data.anomalies?.length > 0 && (
        <div style={st.card}>
          <h2 style={st.h2}>Dikkat Çeken Günler</h2>
          <p style={st.note}>
            Aynı haftagününün son 8 haftasına göre istatistiksel sapma (robust z-skor ≥ 3) — mevsimsellik dikkate alınır
          </p>
          <div className="sx-scroll-x" style={st.scroll}>
            <table style={st.table}>
              <thead>
                <tr>
                  <th style={st.th}>Tarih</th>
                  <th style={st.th}>Metrik</th>
                  <th style={st.th}>Yön</th>
                  <th style={st.thR}>Gerçekleşen</th>
                  <th style={st.thR}>Beklenen</th>
                  <th style={st.thR}>Sapma</th>
                  <th style={st.thR}>z</th>
                </tr>
              </thead>
              <tbody>
                {data.anomalies.slice(0, 12).map((a, i) => (
                  <tr key={i}>
                    <td style={st.td}>{a.date}</td>
                    <td style={st.td}>{a.label}</td>
                    <td style={st.td}>
                      <span style={st.chip(a.severity === 'yuksek')}>
                        {a.direction === 'artis' ? 'Artış' : 'Düşüş'}{a.severity === 'yuksek' ? ' · yüksek' : ''}
                      </span>
                    </td>
                    <td style={st.tdR}>{a.metric === 'revenue' ? money(a.value) : dec(a.value, a.metric === 'conversion_rate' ? 2 : 0)}</td>
                    <td style={st.tdR}>{a.metric === 'revenue' ? money(a.expected) : dec(a.expected, a.metric === 'conversion_rate' ? 2 : 0)}</td>
                    <td style={st.tdR}>{(a.deviation_pct > 0 ? '+' : '') + dec(a.deviation_pct, 1)}%</td>
                    <td style={st.tdR}>{dec(a.z_score, 1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={st.card}>
        <h2 style={st.h2}>Günlük Seyir</h2>
        <p style={st.note}>Oturum ve ciro — {sourceLabel}</p>
        {data.timeseries.length ? <Chart option={trendOption} height={chartH(280)} /> : <Empty text="Bu dönem için veri yok." />}
      </div>

      <div className="sx-grid-panel" style={st.cols}>
        <div style={st.card}>
          <h2 style={st.h2}>Dönüşüm Hunisi</h2>
          <p style={st.note}>{sourceLabel} olay sayıları</p>
          {data.funnel.length ? <Chart option={funnelOption} height={chartH(300)} /> : <Empty text="Huni verisi yok." />}
        </div>
        <div style={st.card}>
          <h2 style={st.h2}>Trafik Kanalları</h2>
          <p style={st.note}>Oturum · dönüşüm için satırın üzerine gelin</p>
          {data.channels.length ? <Chart option={channelOption} height={chartH(300)} /> : <Empty text="Kanal verisi yok." />}
          <SumNote rows={data.channels} total={o.sessions} boyut="kanal" />
        </div>
      </div>

      <div style={st.card}>
        <div style={st.groupHead}><h2 style={st.h2}>Trafik Kaynakları — tüm kanallar</h2></div>
        <p style={st.note}>Kaynak / medium kırılımında oturum, satın alma ve ciro</p>
        <div className="sx-scroll-x" style={st.scroll}>
          <table style={st.table}>
            <thead>
              <tr>
                <th style={st.th}>Kaynak / Medium</th>
                <th style={st.th}>Kanal</th>
                <th style={st.thR}>Oturum</th>
                <th style={st.thR}>Satın alma</th>
                <th style={st.thR}>Dönüşüm</th>
                <th style={st.thR}>Ciro</th>
              </tr>
            </thead>
            <tbody>
              {data.source_medium.map((r, i) => (
                <tr key={i}>
                  <td style={st.td}>{r.source} / {r.medium}</td>
                  <td style={st.tdMuted}>{r.channel}</td>
                  <td style={st.tdR}>{fmt(r.sessions)}</td>
                  <td style={st.tdR}>{fmt(r.purchases)}</td>
                  <td style={st.tdR}>{pct(r.conversion_rate_pct)}</td>
                  <td style={st.tdR}>{money(r.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <SumNote rows={data.source_medium} total={o.sessions} boyut="kaynak" />
        {!data.source_medium.length && <Empty text="Kaynak verisi yok." />}
      </div>

      {(data.social?.length > 0 || data.search_engines?.length > 0) && (
        <div className="sx-grid-panel" style={st.cols}>
          {data.social?.length > 0 && (
            <div style={st.card}>
              <h2 style={st.h2}>Sosyal Ağlar</h2>
              <p style={st.note}>Hangi sosyal platformdan geldiler</p>
              <div className="sx-scroll-x" style={st.scroll}>
                <table style={st.table}>
                  <thead>
                    <tr>
                      <th style={st.th}>Platform</th>
                      <th style={st.thR}>Oturum</th>
                      <th style={st.thR}>Satın alma</th>
                      <th style={st.thR}>Ciro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.social.map((r, i) => (
                      <tr key={i}>
                        <td style={st.td}>{r.name}</td>
                        <td style={st.tdR}>{fmt(r.sessions)}</td>
                        <td style={st.tdR}>{fmt(r.purchases)}</td>
                        <td style={st.tdR}>{money(r.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {data.search_engines?.length > 0 && (
            <div style={st.card}>
              <h2 style={st.h2}>Arama Motorları</h2>
              <p style={st.note}>Organik aramanın kaynağı</p>
              <div className="sx-scroll-x" style={st.scroll}>
                <table style={st.table}>
                  <thead>
                    <tr>
                      <th style={st.th}>Motor</th>
                      <th style={st.thR}>Oturum</th>
                      <th style={st.thR}>Satın alma</th>
                      <th style={st.thR}>Ciro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.search_engines.map((r, i) => (
                      <tr key={i}>
                        <td style={st.td}>{r.name}</td>
                        <td style={st.tdR}>{fmt(r.sessions)}</td>
                        <td style={st.tdR}>{fmt(r.purchases)}</td>
                        <td style={st.tdR}>{money(r.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="sx-grid-panel" style={st.cols}>
        <div style={st.card}>
          <h2 style={st.h2}>Cihaz Dağılımı</h2>
          <p style={st.note}>Oturum payı</p>
          {data.devices.length ? <Chart option={deviceOption} height={chartH(260)} /> : <Empty text="Cihaz verisi yok." />}
        </div>
        <div style={st.card}>
          <h2 style={st.h2}>Şehirler</h2>
          <p style={st.note}>En çok oturum alan 10 şehir</p>
          {data.geo.length ? <Chart option={geoOption} height={chartH(260)} /> : <Empty text="Coğrafya verisi yok." />}
        </div>
      </div>

      {(data.brands.length > 0 || data.products.length > 0) && (
        <>
          <div style={st.groupHead}>
            <span style={st.groupTitle}>Ürün performansı</span>
            <span style={st.groupHint}>{sourceLabel} ürün verisi</span>
          </div>
          {data.brands.length > 0 && (
            <div style={st.card}>
              <h2 style={st.h2}>Yayınevi / Marka Performansı</h2>
              <p style={st.note}>Ciroya göre ilk 10</p>
              <Chart option={brandOption} height={chartH(280)} />
            </div>
          )}
          {data.products.length > 0 && (
            <div style={st.card}>
              <h2 style={st.h2}>En Çok Satan Ürünler</h2>
              <p style={st.note}>Ciroya göre sıralı · sepete ekleme ve satışa dönüşüm ile</p>
              <div className="sx-scroll-x" style={st.scroll}>
                <table style={st.table}>
                  <thead>
                    <tr>
                      <th style={st.th}>Ürün</th>
                      <th style={st.thR}>Görüntüleme</th>
                      <th style={st.thR}>Sepete ekleme</th>
                      <th style={st.thR}>Satış</th>
                      <th style={st.thR}>Sepet→Satış</th>
                      <th style={st.thR}>Ciro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.products.map((p, i) => (
                      <tr key={i}>
                        <td style={st.td}>{p.name}</td>
                        <td style={st.tdR}>{fmt(p.viewed)}</td>
                        <td style={st.tdR}>{fmt(p.added_to_cart)}</td>
                        <td style={st.tdR}>{fmt(p.purchased)}</td>
                        <td style={st.tdR}>{pct(p.cart_to_purchase_pct)}</td>
                        <td style={st.tdR}>{money(p.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {data.pages.length > 0 && (
        <div style={st.card}>
          <h2 style={st.h2}>Giriş Sayfaları</h2>
          <p style={st.note}>Oturum, hemen çıkma ve ortalama süre (günlük ilk 300 sayfa saklanır)</p>
          <div className="sx-scroll-x" style={st.scroll}>
            <table style={st.table}>
              <thead>
                <tr>
                  <th style={st.th}>Sayfa</th>
                  <th style={st.thR}>Oturum</th>
                  <th style={st.thR}>Görüntüleme</th>
                  <th style={st.thR}>Hemen çıkma</th>
                  <th style={st.thR}>Ort. süre</th>
                </tr>
              </thead>
              <tbody>
                {data.pages.map((p, i) => (
                  <tr key={i}>
                    <td style={st.td}>{p.path}</td>
                    <td style={st.tdR}>{fmt(p.sessions)}</td>
                    <td style={st.tdR}>{fmt(p.pageviews)}</td>
                    <td style={st.tdR}>{pct(p.bounce_rate_pct)}</td>
                    <td style={st.tdR}>{dur(p.avg_duration_sec)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.exit_pages?.length > 0 && (
        <div style={st.card}>
          <h2 style={st.h2}>Çıkış Sayfaları</h2>
          <p style={st.note}>Ziyaretçilerin siteyi en çok terk ettiği sayfalar — iyileştirme önceliği</p>
          <div className="sx-scroll-x" style={st.scroll}>
            <table style={st.table}>
              <thead>
                <tr>
                  <th style={st.th}>Sayfa</th>
                  <th style={st.thR}>Çıkış</th>
                </tr>
              </thead>
              <tbody>
                {data.exit_pages.map((p, i) => (
                  <tr key={i}>
                    <td style={st.td}>{p.path}</td>
                    <td style={st.tdR}>{fmt(p.exits || p.sessions)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.campaigns.length > 0 && (
        <div style={st.card}>
          <h2 style={st.h2}>Kampanya Performansı</h2>
          <p style={st.note}>
            {paidCampaigns.length > 0
              ? 'Maliyet yalnızca Google Ads bağlantısı olan kampanyalarda gelir; diğerlerinde ROAS/CPA hesaplanamaz.'
              : 'Bu dönemde maliyet verisi gelmedi — ROAS/CPA hesaplanamıyor.'}
          </p>
          <div className="sx-scroll-x" style={st.scroll}>
            <table style={st.table}>
              <thead>
                <tr>
                  <th style={st.th}>Kampanya</th>
                  <th style={st.thR}>Oturum</th>
                  <th style={st.thR}>Dönüşüm</th>
                  <th style={st.thR}>Ciro</th>
                  <th style={st.thR}>Maliyet</th>
                  <th style={st.thR}>ROAS</th>
                  <th style={st.thR}>CPA</th>
                </tr>
              </thead>
              <tbody>
                {data.campaigns.map((c, i) => (
                  <tr key={i}>
                    <td style={st.td}>{c.campaign}</td>
                    <td style={st.tdR}>{fmt(c.sessions)}</td>
                    <td style={st.tdR}>{fmt(c.conversions)}</td>
                    <td style={st.tdR}>{money(c.revenue)}</td>
                    <td style={st.tdR}>{c.cost > 0 ? money(c.cost) : '—'}</td>
                    <td style={st.tdR}>{c.roas != null ? dec(c.roas, 2) + 'x' : '—'}</td>
                    <td style={st.tdR}>{c.cpa != null ? money(c.cpa) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.events.length > 0 && (
        <div style={st.card}>
          <h2 style={st.h2}>Olaylar</h2>
          <p style={st.note}>En çok tetiklenen olaylar</p>
          <div className="sx-scroll-x" style={st.scroll}>
            <table style={st.table}>
              <thead>
                <tr>
                  <th style={st.th}>Olay</th>
                  <th style={st.thR}>Sayı</th>
                  <th style={st.thR}>Kullanıcı</th>
                </tr>
              </thead>
              <tbody>
                {data.events.map((e, i) => (
                  <tr key={i}>
                    <td style={st.td}>{e.name}</td>
                    <td style={st.tdR}>{fmt(e.count)}</td>
                    <td style={st.tdR}>{fmt(e.users)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  )
}

function CipTab({ executive, customers }) {
  const memberRev = customers?.member_revenue || 0
  const nonRev = customers?.nonmember_revenue || 0
  const totalRev = memberRev + nonRev
  const memberPct = totalRev ? Math.round((memberRev / totalRev) * 100) : 0

  const segmentOption = useMemo(() => {
    const rows = (customers?.segments || []).filter(s => s.revenue > 0).slice(0, 8).sort((a, b) => a.revenue - b.revenue)
    return {
      color: chartPalette,
      textStyle: chartBase.textStyle,
      tooltip: { ...chartBase.tooltip, axisPointer: { type: 'shadow' }, formatter: (ps) => {
        const row = rows[ps[0].dataIndex]
        return `${row.segment}<br/>Ciro: ${money(row.revenue)}<br/>Müşteri: ${fmt(row.customers)}`
      } },
      grid: { left: 8, right: 34, top: 8, bottom: 8, containLabel: true },
      xAxis: { type: 'value', ...axisVal },
      yAxis: { type: 'category', data: rows.map(r => r.segment), ...axisCat },
      series: [{
        type: 'bar', data: rows.map(r => r.revenue), barMaxWidth: 18,
        itemStyle: { color: lineGradient, borderRadius: [0, 4, 4, 0] },
        label: { show: true, position: 'right', color: colors.textSecondary, fontSize: 11, formatter: (p) => money(p.value) },
      }],
    }
  }, [customers])

  if (!executive || !customers) return <Empty text="Ticaret verisi yüklenemedi." />

  return (
    <>
      <div style={st.groupHead}>
        <span style={st.groupTitle}>Seçili dönem</span>
        <span style={st.groupHint}>widget ile izlenen sipariş · tarih aralığıyla değişir</span>
      </div>
      <KpiGrid items={[
        { label: 'Dönem cirosu (izlenen)', val: money(executive.tracked_revenue) },
        { label: 'Dönem siparişi (izlenen)', val: fmt(executive.tracked_orders) },
        { label: 'Funnel dönüşümü', val: pct(executive.funnel_conversion_pct) },
        { label: 'Oturum (GA4)', val: fmt(executive.ga4_sessions) },
      ]} />

      <div style={st.groupHead}>
        <span style={st.groupTitle}>Yaşam boyu portföy</span>
        <span style={st.groupHint}>CIP · tarih aralığından bağımsız</span>
      </div>
      <KpiGrid items={[
        { label: 'Toplam ciro', val: money(executive.lifetime_revenue) },
        { label: 'Toplam sipariş', val: fmt(executive.lifetime_orders) },
        { label: 'Müşteri', val: fmt(executive.customers) },
        { label: 'Ort. müşteri değeri', val: money(executive.avg_customer_value) },
      ]} />

      <div style={st.card}>
        <h2 style={st.h2}>Müşteri Değer Segmentleri</h2>
        <p style={st.note}>CIP · segment başına gerçek ciro</p>
        <Chart option={segmentOption} height={chartH(300)} />
        <div style={st.split}>
          <div style={st.seg}>
            <div style={st.segTop}><span style={st.dot} />Üye müşteri</div>
            <div style={st.segVal}>{money(memberRev)}</div>
            <div style={st.segMeta}>cironun %{memberPct}'i</div>
          </div>
          <div style={{ ...st.seg, borderLeft: `1px solid ${colors.border}` }}>
            <div style={st.segTop}><span style={st.dot} />Üye değil</div>
            <div style={st.segVal}>{money(nonRev)}</div>
            <div style={st.segMeta}>cironun %{100 - memberPct}'i</div>
          </div>
        </div>
      </div>
    </>
  )
}

export default function SmartAnalysis() {
  useIsMobile()
  const { selectedBrand } = useBrand()
  const [tab, setTab] = useState('ga4')
  const [from, setFrom] = useState(isoDaysAgo(7))
  const [to, setTo] = useState(isoDaysAgo(1))
  const [store, setStore] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  const [insights, setInsights] = useState({})
  const [aiLoading, setAiLoading] = useState('')
  const [aiError, setAiError] = useState('')
  const [briefing, setBriefing] = useState(null)
  const [briefingBusy, setBriefingBusy] = useState(false)
  const [question, setQuestion] = useState('')
  const [thread, setThread] = useState([])
  const [openIdx, setOpenIdx] = useState(null)
  const [asking, setAsking] = useState(false)
  const [askError, setAskError] = useState('')

  const brandId = selectedBrand?.id
  const headers = { Authorization: localStorage.getItem('adminToken') }

  const load = useCallback(async (which) => {
    if (!brandId) return
    setLoading(true)
    setError('')
    try {
      const opts = (extra) => ({ headers, timeout: 30000, params: { brand_id: brandId, ...extra } })
      if (which === 'cip') {
        const [ex, cu] = await Promise.all([
          axios.get(`${ANALYTICS}/api/v1/metrics/executive`, opts({ from, to })),
          axios.get(`${ANALYTICS}/api/v1/metrics/customers`, opts({})),
        ])
        setStore(prev => ({ ...prev, cip: { executive: ex.data, customers: cu.data } }))
      } else {
        const [res, anom] = await Promise.all([
          axios.get(`${ANALYTICS}/api/v1/analytics/${which}`, opts({ from, to })),
          axios.get(`${ANALYTICS}/api/v1/anomalies`, opts({ from, to, source: which })).catch(() => ({ data: { anomalies: [] } })),
        ])
        setStore(prev => ({ ...prev, [which]: { ...res.data, anomalies: anom.data.anomalies || [] } }))
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Veri alınamadı. Smart Analysis servisi çalışıyor mu?')
    } finally {
      setLoading(false)
    }
  }, [brandId, from, to]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchKey = useRef('')
  useEffect(() => {
    const key = `${brandId}|${from}|${to}|${tab}`
    if (!brandId || fetchKey.current === key) return
    fetchKey.current = key
    load(tab)
  }, [brandId, from, to, tab, load])

  useEffect(() => {
    setStore({})
    setInsights({})
    fetchKey.current = ''
  }, [brandId, from, to])

  const refresh = useCallback(async () => {
    if (!brandId || tab === 'cip') return
    setRefreshing(true)
    setError('')
    try {
      await axios.post(`${ANALYTICS}/api/v1/etl/refresh`, null, {
        headers, timeout: 180000, params: { brand_id: brandId, source: tab, days: 2 },
      })
      fetchKey.current = ''
      await load(tab)
    } catch (e) {
      setError(e.response?.data?.detail || 'Güncelleme başarısız.')
    } finally {
      setRefreshing(false)
    }
  }, [brandId, tab, load]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadInsight = useCallback(async (scope) => {
    if (!brandId) return
    setAiLoading(scope)
    setAiError('')
    try {
      const res = await axios.get(`${ANALYTICS}/api/v1/insights`, {
        headers, timeout: 60000, params: { brand_id: brandId, from, to, scope },
      })
      setInsights(prev => ({ ...prev, [scope]: res.data }))
    } catch (e) {
      setAiError(e.response?.data?.detail || 'AI yorumu alınamadı.')
    } finally {
      setAiLoading('')
    }
  }, [brandId, from, to]) // eslint-disable-line react-hooks/exhaustive-deps

  const ask = useCallback(async (text) => {
    const q = (text || '').trim()
    if (!brandId || !q) return
    setAsking(true)
    setAskError('')
    setQuestion('')
    try {
      const history = thread.map(t => ({ question: t.question, answer: t.answer }))
      const res = await axios.post(
        `${ANALYTICS}/api/v1/agent/ask`,
        { question: q, source: tab === 'cip' ? 'ga4' : tab, history },
        { headers, timeout: 120000, params: { brand_id: brandId, from, to } },
      )
      setThread(prev => [...prev, {
        question: q,
        answer: res.data.answer,
        suggestions: res.data.suggestions || [],
        offTopic: res.data.off_topic,
        tools: res.data.used_tools || [],
      }])
      setOpenIdx(null)
    } catch (e) {
      setAskError(e.response?.data?.detail || 'Soru yanıtlanamadı.')
    } finally {
      setAsking(false)
    }
  }, [brandId, tab, from, to, thread]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!brandId) return
    let cancelled = false
    axios.get(`${ANALYTICS}/api/v1/briefing`, { headers, timeout: 25000, params: { brand_id: brandId } })
      .then(res => { if (!cancelled) setBriefing(res.data.briefing || null) })
      .catch(() => { if (!cancelled) setBriefing(null) })
    return () => { cancelled = true }
  }, [brandId]) // eslint-disable-line react-hooks/exhaustive-deps

  const generateBriefing = useCallback(async () => {
    if (!brandId) return
    setBriefingBusy(true)
    try {
      const res = await axios.post(`${ANALYTICS}/api/v1/briefing/generate`, null, {
        headers, timeout: 120000, params: { brand_id: brandId },
      })
      setBriefing(res.data.briefing)
    } catch (e) {
      setError(e.response?.data?.detail || 'Günlük durum hazırlanamadı.')
    } finally {
      setBriefingBusy(false)
    }
  }, [brandId]) // eslint-disable-line react-hooks/exhaustive-deps

  const shownIdx = openIdx == null ? thread.length - 1 : openIdx
  const shown = thread[shownIdx]
  const isLatest = shownIdx === thread.length - 1

  const active = store[tab]
  const activeTab = TABS.find(t => t.key === tab)
  const shownInsight = insights[tab]
  const combined = insights.combined

  return (
    <div>
      <style>{KEYFRAMES}</style>
      <div style={st.header}>
        <div>
          <h1 style={st.title}>Smart Analysis</h1>
          <p style={st.sub}>{selectedBrand ? selectedBrand.name : 'Marka seçilmedi'} · kaynaklar ayrı sekmelerde tutulur</p>
        </div>
        <div style={st.controls}>
          <input type="date" value={from} min={isoDaysAgo(SAKLAMA_GUN)} onChange={e => setFrom(e.target.value)} style={st.date} />
          <span style={{ color: colors.textMuted }}>–</span>
          <input type="date" value={to} min={isoDaysAgo(SAKLAMA_GUN)} onChange={e => setTo(e.target.value)} style={st.date} />
          {tab !== 'cip' && (
            <button style={st.refresh(refreshing)} onClick={refresh} disabled={refreshing}>
              {refreshing ? 'Güncelleniyor…' : 'Şimdi güncelle'}
            </button>
          )}
        </div>
      </div>

      {selectedBrand && (
        <Briefing data={briefing} onGenerate={generateBriefing} busy={briefingBusy} />
      )}

      <div style={st.tabs}>
        {TABS.map(t => (
          <button key={t.key} style={st.tab(tab === t.key)} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      <div style={st.meta}>
        <span>{activeTab.note}</span>
        {active?.freshness?.last_date && <span>· son veri: {active.freshness.last_date}</span>}
        {tab === 'cip' && <span>· portföy verisi gece senkronundan gelir</span>}
      </div>

      {selectedBrand && (
        <div style={st.ai}>
          <div style={st.aiHead}>
            <span style={st.aiTitle}>Open AI Yorumu</span>
            <div style={st.aiBtns}>
              <button style={st.aiBtn(aiLoading === tab)} onClick={() => loadInsight(tab)} disabled={!!aiLoading}>
                {aiLoading === tab ? 'Analiz ediliyor…' : `${activeTab.label} yorumu`}
              </button>
              <button style={st.aiBtn(aiLoading === 'combined', true)} onClick={() => loadInsight('combined')} disabled={!!aiLoading}>
                {aiLoading === 'combined' ? 'Analiz ediliyor…' : 'Birleşik yorum'}
              </button>
            </div>
          </div>
          {aiError && <p style={{ fontSize: '13px', color: colors.dangerText, margin: '12px 0 0' }}>{aiError}</p>}
          {(shownInsight || combined) && (() => {
            const shown = shownInsight || combined
            return (
              <>
                <p style={st.aiHeadline}>{shown.headline}</p>
                <p style={st.aiSummary}>{shown.summary}</p>
                <div className="sx-grid-panel" style={st.aiCols}>
                  <div>
                    <p style={st.aiColTitle}>Bulgular</p>
                    <ul style={st.aiList}>{(shown.findings || []).map((f, i) => <li key={i} className="sx-break" style={st.aiItem}>{f}</li>)}</ul>
                  </div>
                  <div>
                    <p style={st.aiColTitle}>Öneriler</p>
                    <ul style={st.aiList}>{(shown.recommendations || []).map((r, i) => <li key={i} className="sx-break" style={st.aiItem}>{r}</li>)}</ul>
                  </div>
                </div>
              </>
            )
          })()}
        </div>
      )}

      {selectedBrand && (
        <AskPanel
          thread={thread}
          shown={shown}
          shownIdx={shownIdx}
          isLatest={isLatest}
          onSelect={setOpenIdx}
          onClear={() => { setThread([]); setOpenIdx(null); setAskError('') }}
          question={question}
          setQuestion={setQuestion}
          asking={asking}
          askError={askError}
          onAsk={ask}
        />
      )}

      {!selectedBrand && <div style={st.msg}>Önce üstten bir marka seçin.</div>}
      {selectedBrand && loading && <div style={st.msg}>Yükleniyor…</div>}
      {selectedBrand && !loading && error && (
        <div style={st.msgError}>{error}</div>
      )}

      {selectedBrand && !loading && !error && active && tab !== 'cip' && (
        <SourceTab data={active} sourceLabel={activeTab.label} />
      )}
      {selectedBrand && !loading && !error && active && tab === 'cip' && (
        <CipTab executive={active.executive} customers={active.customers} />
      )}
    </div>
  )
}
